import { VersionedDialog } from '@/components/admin/versioned/versioned-dialog';
import type { VersionedWording } from '@/lib/versioned-labels';
import {
  DISCARD_DRAFT_LABEL,
  DISCARD_DRAFT_REASSURANCE,
  discardDraftDescription,
} from '@/lib/versioned-labels';
import { Alert } from '@pms-digital/design-system';

/**
 * The confirmation of « Abandonner le brouillon » (refonte modulaire §4, issue
 * #185).
 *
 * ## It says WHO and WHEN, because the draft may not be the operator's
 *
 * The server draft is shared: last write wins over a draft the writer has
 * seen, and the head already tells who touched it last. Throwing it away is the
 * one gesture of the screen that destroys typing with no way back - the
 * versions are untouched, the draft is not a version - so the box names the
 * author and the day before the click, and the version the editor will start
 * again from after it. It is the destructive tone for the same reason the
 * retirement's is, and a gesture that cannot be undone should not be the one
 * that takes the default button: « Annuler » keeps the focus
 * (`versioned-dialog.tsx`).
 *
 * No referent list, unlike its three siblings: nothing plays a draft. The
 * pointers do not move, so there is no effect on who reads what to show.
 */

export interface DiscardDialogProps {
  wording: VersionedWording;
  /** What the server holds: who changed the draft last, and when. */
  draft: { readonly updatedAt: string; readonly updatedBy: string };
  /** The version the editor will start again from. */
  developmentNumber: number;
  busy: boolean;
  error: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

export function DiscardDialog({
  wording,
  draft,
  developmentNumber,
  busy,
  error,
  onConfirm,
  onCancel,
}: DiscardDialogProps) {
  return (
    <VersionedDialog
      data-testid={`${wording.entity}-boite-abandon`}
      title={`${DISCARD_DRAFT_LABEL} ?`}
      description={discardDraftDescription({
        updatedBy: draft.updatedBy,
        updatedAt: draft.updatedAt,
        developmentNumber,
      })}
      confirmLabel={DISCARD_DRAFT_LABEL}
      confirmTone="destructive"
      busy={busy}
      error={error}
      onConfirm={onConfirm}
      onCancel={onCancel}
    >
      <Alert tone="warning">{DISCARD_DRAFT_REASSURANCE}</Alert>
    </VersionedDialog>
  );
}
